import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useApp } from '../context/AppContext';
import api from '../services/api';
import Sidebar from './Sidebar';
import './Dialog.css';
import useAuth from '../hooks/useAuth';

const Dialog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, selectedCountry, selectedTopic, countries, topics, currentChat } = useApp();
  const [userMessage, setUserMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const initialMessageSent = useRef(false);

  useAuth(); // This handles all authentication and redirects

  useEffect(() => {
    if (location.state && !initialMessageSent.current) {
      const { initialMessage } = location.state;
      setUserMessage(initialMessage);
      handleSendMessage(initialMessage);
      initialMessageSent.current = true;
    }
  }, [location.state]);

  const handleSendMessage = async (message) => {
    const messageToSend = message || userMessage;
    
    if (messageToSend.trim()) {
      // Add user message
      const newUserMessage = {
        role: 'user',
        content: messageToSend,
        topic: selectedTopic,
        country: selectedCountry
      };
      
      const updatedChatHistory = [...messages, newUserMessage];
      setMessages(updatedChatHistory);
      setUserMessage('');
      setIsLoading(true);
      
      try {
        // Get response from API
        const response = await api.sendMessage(
          updatedChatHistory,
          messageToSend,
          selectedTopic,
          selectedCountry
        );
        
        // Add response to messages
        const botMessage = {
          role: 'assistant',
          content: response.answer,
        };
        setMessages([...updatedChatHistory, botMessage]);
      } catch (error) {
        console.error('Error sending message:', error);
        // Add error message
        setMessages(prevMessages => [
          ...prevMessages, 
          {
            role: 'assistant',
            content: 'Sorry, there was an error processing your request. Please try again.'
          }
        ]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleFeedback = async (type) => {
    setFeedback(type);
    
    try {
      // Send feedback to API
      await api.sendFeedback(messages[messages.length - 1].id, type);
    } catch (error) {
      console.error('Error sending feedback:', error);
    }
  };

  // Find the selected country name
  const selectedCountryName = selectedCountry ? 
    countries.find(c => c.id === selectedCountry)?.name || 'United States' : 
    'United States';

  // Find the selected topic name
  const selectedTopicName = selectedTopic && topics.length > 0 ? 
    topics.find(t => t.id === selectedTopic)?.name || 'Business Law' : 
    'Business Law';

  return (
    <div className="dialog-container">
      <Sidebar />
      
      <div className="main-content">
        
        <div className="messages-container">
          {messages.map((message, index) => (
            <div key={index} className={`message ${message.role}`}>
              <div 
                className="message-bubble" 
                style={{whiteSpace: "pre-wrap", textAlign: "justify"}}
                dangerouslySetInnerHTML={{__html: message.content}}
              />
              
              {message.role === 'assistant' && (
                <div className="feedback-container">
                  <div 
                    className={`feedback-button ${feedback === 1 ? 'active' : ''}`}
                    onClick={() => handleFeedback(1)}
                  >
                    <img src="/assets/dialogassets/Dialog_thumbs up selection-14.svg" alt="Like" />
                  </div>
                  <div 
                    className={`feedback-button ${feedback === 0 ? 'active' : ''}`}
                    onClick={() => handleFeedback(0)}
                  >
                    <img src="/assets/dialogassets/Dialog_thumbs down selection-15.svg" alt="Dislike" />
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {isLoading && (
            <div className="message cola">
              <div className="message-bubble loading">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </div>
            </div>
          )}
        </div>
        
        <div className="input-container">
          <textarea 
            placeholder="Type your message here..." 
            value={userMessage}
            onChange={(e) => setUserMessage(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            disabled={isLoading}
          ></textarea>
          <button 
            className="send-button" 
            onClick={() => handleSendMessage()}
            disabled={isLoading}
          >
            →
          </button>
        </div>
        
        <div className="note-section">
          <p>Note: The information provided is for general guidance only and should not be relied upon as legal or tax advice. Please consult with qualified professionals for advice specific to your situation.</p>
        </div>
      </div>
    </div>
  );
};

export default Dialog;
